import React, { useState, useEffect } from 'react';
import SEO from '../SEO/SEO';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Briefcase, Users, TrendingUp, Award, Shield, Clock, BookOpen, FileText, CheckCircle2, ArrowRight, User, Building2, CreditCard, MapPin, Camera, FileCheck, Star } from 'lucide-react';
import './DSAOnboarding.css';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import EnquiryModal from '../Modals/EnquiryModal';

const sanitizeAndFormatHtml = (content) => {
  if (!content || typeof content !== 'string') return '';
  let html = content;
  html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
    const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
    if (!attrMatch) return tag;
    const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
    const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
    const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
    return `<a href="${rawUrl}"${targetAttr}>`;
  });
  if (!/<(?:br|p|div)\b/i.test(html)) {
    html = html.replace(/\r?\n/g, '<br />');
  }
  return html;
};

const DEFAULT_CTA_BOTTOM_TEXT = 'Learn more about the <a href="https://ahamhfc.com/become-home-loan-dsa-partner">Home Loan DSA program</a>, including the partnership process and requirements.<br /><br />Explore how to <a href="https://ahamhfc.com/become-home-loan-dsa-partner">become a Home Loan DSA partner</a> and review the partnership requirements.';

const DSAOnboarding = ({ onEnquiryClick }) => {
  const [isLocalModalOpen, setIsLocalModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleRichTextClick = (e) => {
    const target = e.target.closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    const linkTarget = target.getAttribute('target');
    if (!href || linkTarget === '_blank') return;

    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    } else if (href.startsWith('https://ahamhfc.com/') || href.startsWith('http://ahamhfc.com/')) {
      const path = href.replace(/^https?:\/\/ahamhfc\.com/, '');
      if (path.startsWith('/')) {
        e.preventDefault();
        navigate(path);
      }
    }
  };

  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [benefits, setBenefits] = useState([]);
  const [benefitsSettings, setBenefitsSettings] = useState({
    section_title: 'Benefits of Becoming a DSA',
    section_subtitle: 'Why Choose Aham',
    section_description: 'We believe in building partnerships that create shared value. When you onboard with Aham, you gain transparent processes, timely payouts, and end-to-end support.'
  });
  const [steps, setSteps] = useState([]);
  const [stepsSettings, setStepsSettings] = useState({
    section_title: 'Simple and Streamlined Onboarding',
    section_subtitle: 'Getting Started',
    section_description: 'We follow a clear, structured onboarding process to ensure compliance and smooth engagement from day one.'
  });
  const [eligibility, setEligibility] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [requirementsSettings, setRequirementsSettings] = useState({
    section_title: 'Who Can Apply & What You Need',
    section_subtitle: 'Requirements',
    cta_text: 'To ensure transparency and regulatory compliance, we require basic documentation and verification.',
    cta_link: 'All applications are subject to due diligence and approval.'
  });
  const [ctaSettings, setCtaSettings] = useState({
    title: 'Ready to Partner With Us?',
    description: 'Start your journey as an Aham DSA partner today and help customers achieve their dreams while growing your business.',
    button_text: 'Contact Team',
    button_link: '/contact',
    bottom_text: DEFAULT_CTA_BOTTOM_TEXT
  });

  const iconMap = {
    TrendingUp, Shield, Users, Clock, BookOpen, Award, FileText, FileCheck, CheckCircle2,
    CreditCard, Camera, MapPin, Building2, User
  };

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/admin/banners/page/dsa_onboarding');
        const bannerData = response.data.data;
        if (bannerData) {
          if (Array.isArray(bannerData) && bannerData.length > 0) {
            setBanner(bannerData[0]);
          } else if (!Array.isArray(bannerData)) {
            setBanner(bannerData);
          }
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBanner();
  }, []);

  useEffect(() => {
    const fetchBenefits = async () => {
      try {
        const [benefitsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/key-benefits/dsa_onboarding/active'),
          axios.get('/api/admin/key-benefits/settings/dsa_onboarding')
        ]);
        if (benefitsRes.data.success) {
          setBenefits(benefitsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setBenefitsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching benefits:', error);
      }
    };

    const fetchSteps = async () => {
      try {
        const [stepsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/key-benefits/dsa_onboarding_steps/active'),
          axios.get('/api/admin/key-benefits/settings/dsa_onboarding_steps')
        ]);
        if (stepsRes.data.success) {
          setSteps(stepsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setStepsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching steps:', error);
      }
    };

    const fetchRequirements = async () => {
      try {
        const [settingsRes, eligibilityRes, documentsRes] = await Promise.all([
          axios.get('/api/admin/key-benefits/settings/dsa_requirements'),
          axios.get('/api/admin/key-benefits/dsa_eligibility/active'),
          axios.get('/api/admin/key-benefits/dsa_documents/active')
        ]);
        if (settingsRes.data.success && settingsRes.data.data) {
          setRequirementsSettings(settingsRes.data.data);
        }
        if (eligibilityRes.data.success) {
          setEligibility(eligibilityRes.data.data || []);
        }
        if (documentsRes.data.success) {
          setDocuments(documentsRes.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching requirements:', error);
      }
    };

    const fetchCTA = async () => {
      try {
        const response = await axios.get('/api/admin/cta-banner/settings/dsa_onboarding_cta');
        if (response.data.data) {
          setCtaSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA:', error);
      }
    };

    fetchBenefits();
    fetchSteps();
    fetchRequirements();
    fetchCTA();
  }, []);

  const partnerTypes = [
    {
      icon: <User className="w-7 h-7" />,
      title: 'Independent loan consultants',
      color: 'blue'
    },
    {
      icon: <Briefcase className="w-7 h-7" />,
      title: 'Financial service professionals',
      color: 'gold'
    },
    {
      icon: <Building2 className="w-7 h-7" />,
      title: 'Small and mid-sized business owners',
      color: 'green'
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: 'Real estate advisors and channel partners',
      color: 'purple'
    }
  ];

  const staticBenefits = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Competitive Payouts',
      description: 'Competitive & timely payout structures that value your contribution'
    },
    // ... rest of static benefits if needed (truncated for brevity in diff but in real file keeps same)
    // Actually MultiReplace replaces chunk. I will just reference existing 'staticBenefits' but update 'displayBenefits' below.
    // Wait, I need to keep staticBenefits intact if I don't replace it.
    // I am not changing staticBenefits here, just making sure I don't break surrounding code.
    // The previous Tool Call 117 replaced lines 53-85 with 'staticBenefits'.
    // I will skip replacing staticBenefits content and just target 'displayBenefits'.

    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Competitive Payouts',
      description: 'Competitive & timely payout structures that value your contribution'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Transparent System',
      description: 'Transparent approval and processing system with no hidden conditions'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Dedicated Support',
      description: 'Dedicated support from our relationship teams at every step'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Quick Processing',
      description: 'Quick and secure digital documentation workflows'
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: 'Training & Knowledge',
      description: 'Access to training & product knowledge sessions'
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Growth Opportunities',
      description: 'Recognition programs and performance incentives'
    }
  ];

  const displayBenefits = benefits.length > 0 ? benefits : staticBenefits;

  const staticSteps = [
    {
      number: '01',
      title: 'Submit DSA Application',
      description: 'Apply online or through our representative',
      icon: <FileText className="w-6 h-6" />
    },
    {
      number: '02',
      title: 'Document Verification',
      description: 'Due diligence & verification process',
      icon: <FileCheck className="w-6 h-6" />
    },
    {
      number: '03',
      title: 'Agreement Signing',
      description: 'Agreement signing & code allocation',
      icon: <Shield className="w-6 h-6" />
    },
    {
      number: '04',
      title: 'Training Session',
      description: 'Onboarding & product knowledge training',
      icon: <BookOpen className="w-6 h-6" />
    },
    {
      number: '05',
      title: 'Start Sourcing',
      description: 'Begin sourcing eligible customer leads',
      icon: <TrendingUp className="w-6 h-6" />
    }
  ];

  const displaySteps = steps.length > 0 ? steps : staticSteps;

  const staticEligibilityCriteria = [
    'Individual or registered entity',
    'Good financial track record',
    'Understanding of lending and customer service'
  ];

  const staticDocuments = [
    {
      icon: <CreditCard className="w-5 h-5" />,
      title: 'PAN & Aadhaar',
      subtitle: 'Individual / Authorized Signatory'
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: 'GST Certificate / Business Registration',
      subtitle: 'If applicable'
    },
    {
      icon: <CheckCircle2 className="w-5 h-5" />,
      title: 'Cancelled Cheque',
      subtitle: 'For payouts'
    },
    {
      icon: <Camera className="w-5 h-5" />,
      title: 'Passport-size Photograph',
      subtitle: 'Recent photograph'
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      title: 'Address Proof',
      subtitle: 'Individual or registered office'
    },
    {
      icon: <Building2 className="w-5 h-5" />,
      title: 'Partnership/Company Documents',
      subtitle: 'For firms'
    }
  ];

  const displayEligibility = eligibility.length > 0 ? eligibility : staticEligibilityCriteria;
  const displayDocuments = documents.length > 0 ? documents : staticDocuments;

  return (
    <div className="dsa-onboarding">

      <SEO
        title="Home Loan DSA Registration: Apply Online | Aham Housing Finance"
        description="Complete your Home Loan DSA registration online with Aham Housing Finance. Simple onboarding, quick document upload, and instant partner ID. Start earning now!!"
        keywords="Aham Housing Finance DSA onboarding, Aham DSA onboarding, DSA onboarding process, home loan DSA onboarding, loan DSA onboarding, direct selling agent onboarding, housing finance DSA onboarding, DSA registration process, home loan agent onboarding, loan partner onboarding, channel partner onboarding, mortgage DSA onboarding, housing loan partner registration, DSA KYC process, DSA document verification, Aham partner onboarding, home loan referral partner onboarding, affordable housing finance partner, DSA application process, become a DSA partner"
        canonical="https://ahamhfc.com/home-loan-dsa-registration-online"
        ogTitle="Home Loan DSA Registration: Apply Online | Aham Housing Finance"
        ogDescription="Complete your Home Loan DSA registration online with Aham Housing Finance. Simple onboarding, quick document upload, and instant partner ID. Start earning now!"
      />

      {/* Hero Banner */}
      {/* Hero Banner */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {loading ? (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="animate-pulse flex flex-col items-center">
              <div className="h-8 w-48 bg-white/20 rounded mb-4"></div>
              <div className="h-4 w-64 bg-white/10 rounded"></div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('${banner?.image_url || '/img/Banners/Onboarding-Banner.webp'}')`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white mb-6 border border-white/20">
                  <Briefcase className="w-5 h-5" />
                  <span className="font-medium">Partnership Opportunity</span>
                </div>
                <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title || 'DSA Onboarding'}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-6 md:mb-8 leading-relaxed max-w-2xl">
                  {banner?.subtitle || 'Partner with Aham to deliver financial solutions that create real impact.'}
                </p>

                {/* Breadcrumb Navigation */}
                <div className="flex items-center space-x-2">
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">DSA Onboarding</span>
                </div>

              </motion.div>
            </div>
          </>
        )}
      </section>

      {/* Section 1: Who Can Partner */}
      {/* <section className="partner-section">
        <div className="section-container">
          <div className="section-header-center">
            <span className="section-tag">Partnership Criteria</span>
            <h2 className="section-title">Build With Us</h2>
            <p className="section-description">
              We invite committed individuals and organizations to join us as Direct Selling Agents (DSAs).
              Whether you're a freelance loan consultant, a financial advisor, or a business entity with customer reach —
              this partnership helps you serve more customers and grow your business sustainably.
            </p>
          </div>

          <div className="partner-types-grid">
            {partnerTypes.map((partner, index) => (
              <Card key={index} className={`partner-type-card ${partner.color}`}>
                <div className="partner-icon-wrapper">{partner.icon}</div>
                <p className="partner-type-title">{partner.title}</p>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* Section 2: Benefits */}
      <section className="benefits-section">
        <div className="section-container">
          <div className="section-header-center">
            <span className="section-tag">{benefitsSettings.section_subtitle}</span>
            <h2 className="section-title">{benefitsSettings.section_title}</h2>
            <p className="section-description">
              {benefitsSettings.section_description || 'We believe in building partnerships that create shared value. When you onboard with Aham, you gain transparent processes, timely payouts, and end-to-end support.'}
            </p>
          </div>

          <div className="benefits-grid">
            {displayBenefits.map((benefit, index) => {
              // Resolve icon: if dynamic used icon_name string, map it. If static, it has 'icon' jsx.
              let IconElement = benefit.icon;
              if (!IconElement && benefit.icon_name) {
                const IconComponent = iconMap[benefit.icon_name] || Star;
                IconElement = <IconComponent className="w-6 h-6" />;
              }

              return (
                <Card key={index} className="benefit-card">
                  <div className="benefit-icon">{IconElement}</div>
                  <h3 className="benefit-title">{benefit.title}</h3>
                  <p className="benefit-description">{benefit.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 3: Onboarding Process */}
      <section className="process-section">
        <div className="section-container">
          <div className="section-header-center">
            <span className="section-tag">{stepsSettings.section_subtitle}</span>
            <h2 className="section-title">{stepsSettings.section_title}</h2>
            <p className="section-description">
              {stepsSettings.section_description || 'We follow a clear, structured onboarding process to ensure compliance and smooth engagement from day one.'}
            </p>
          </div>

          <div className="process-steps-compact">
            {displaySteps.map((step, index) => {
              // Resolve icon
              let IconElement = step.icon;
              if (!IconElement && step.icon_name) {
                const IconComponent = iconMap[step.icon_name] || FileText;
                IconElement = <IconComponent className="w-6 h-6" />;
              }
              // Resolve number (from gradient_from or default)
              const stepNumber = step.gradient_from || step.number || `0${index + 1}`;

              return (
                <React.Fragment key={index}>
                  <div className="process-step-compact">
                    <div className="step-number-compact">{stepNumber}</div>
                    <div className="step-icon-compact">{IconElement}</div>
                    <h3 className="step-title-compact">{step.title}</h3>
                    <p className="step-desc-compact">{step.description}</p>
                  </div>
                  {index < displaySteps.length - 1 && (
                    <div className="step-arrow">
                      <ArrowRight className="w-6 h-6" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4: Eligibility & Documents */}
      <section className="eligibility-section">
        <div className="section-container">
          <div className="section-header-center">
            <span className="section-tag">{requirementsSettings.section_subtitle}</span>
            <h2 className="section-title">{requirementsSettings.section_title}</h2>
            <p className="section-description">
              {requirementsSettings.cta_text}
            </p>
          </div>

          <div className="eligibility-layout">
            <Card className="eligibility-card">
              <h3 className="eligibility-heading">Eligibility Criteria</h3>
              <ul className="eligibility-list">
                {displayEligibility.map((criteria, index) => (
                  <li key={index} className="eligibility-item">
                    <CheckCircle2 className="eligibility-icon" />
                    <span>{criteria.title || criteria}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="documents-card">
              <h3 className="documents-heading">Documents Required</h3>
              <div className="documents-grid">
                {displayDocuments.map((doc, index) => {
                  // Resolve icon
                  let IconElement = doc.icon;
                  if (!IconElement && doc.icon_name) {
                    const IconComponent = iconMap[doc.icon_name] || FileText;
                    IconElement = <IconComponent className="w-5 h-5" />;
                  }

                  return (
                    <div key={index} className="document-item">
                      <div className="document-icon">{IconElement}</div>
                      <div className="document-info">
                        <p className="document-title">{doc.title}</p>
                        <p className="document-subtitle">{doc.description || doc.subtitle}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="documents-note">
                <Shield className="note-icon" />
                <p className="note-text">
                  <strong>Note:</strong> {requirementsSettings.cta_link || 'All applications are subject to due diligence and approval.'}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="dsa-cta">
        <div className="cta-container-dsa">
          <div className="cta-content-dsa">
            <h2 className="cta-title-dsa">{ctaSettings.title}</h2>
            <p className="cta-text-dsa">
              {ctaSettings.description}
            </p>
            {(ctaSettings.bottom_text || ctaSettings.bottom_text === undefined) && (
              <div
                onClick={handleRichTextClick}
                className="cta-notice-dsa text-base md:text-lg text-white/95 max-w-3xl mx-auto mb-10 leading-relaxed [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold [&_a]:transition-colors cursor-pointer"
                dangerouslySetInnerHTML={{
                  __html: sanitizeAndFormatHtml(
                    ctaSettings.bottom_text || DEFAULT_CTA_BOTTOM_TEXT
                  )
                }}
              />
            )}
            <div className="cta-buttons-dsa">
              <Button
                variant="outline"
                className="cta-btn-primary-dsa"
                onClick={() => {
                  if (onEnquiryClick) {
                    onEnquiryClick();
                  } else {
                    setIsLocalModalOpen(true);
                  }
                }}
              >
                {ctaSettings.button_text}
              </Button>

              {/* <Button className="cta-btn-primary-dsa">
                Begin Application
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button variant="outline" className="cta-btn-secondary-dsa">
                Contact Team
              </Button> */}
            </div>
          </div>
        </div>
      </section>
      {/* Fallback Enquiry Modal if parent doesn't provide it */}
      {!onEnquiryClick && (
        <EnquiryModal
          isOpen={isLocalModalOpen}
          onClose={() => setIsLocalModalOpen(false)}
        />
      )}
    </div>
  );
};

export default DSAOnboarding;